import { SHARE_PARAM_KEY } from "./webShareLink";

// Canonical detail-page URL.
//
// Once the package details API resolves, the detail page swaps the address bar
// for the compressed share URL (/link?data=... — see src/utils/webShareLink.js)
// via history.replaceState. From that point the address bar is display-only and
// no longer carries the query params the page runs on, so the readable
// /hotel/query?... URL is preserved here for the lifetime of the page.
//
// Module scope rather than a React ref on purpose: the plain helpers in
// helpers/common.js (replaceQueryParam / getQueryParamValue) have no React
// context, and DetailsTopBar / DetailsCard are siblings of the detail page.
//
// Being in memory also makes this the signal for who owns the /link route: a
// pasted share link always starts a fresh JS context, so a live value here means
// "this session already has a detail page" (see src/routes/index.js).
// history.state or sessionStorage would survive a reload and get that wrong.
// The URL outlives the detail page: pages opened from it (gallery, map) need it
// to navigate back, because the history entry they would pop to now holds the
// share URL. It is replaced wholesale the next time a detail page is opened.
let canonicalDetailUrl = null;
// Whether a detail page is currently mounted. Tracked separately from the URL
// because it answers a different question — who owns the /link route right now.
let detailSessionLive = false;

// History index of the entry this JS context started on, when that entry is a
// pasted share link (/link?data=...); null otherwise.
//
// It matters for going back: once a detail page exists, /link renders the detail
// page rather than Home (see src/routes/index.js), and the detail page swaps its
// own address bar for that same share URL. So popping back onto the landing entry
// re-renders the page the user is already looking at — it is not a real back
// destination and callers must skip past it.
//
// Captured explicitly rather than in a module-load IIFE.
//
// The IIFE only worked because nothing in this app is code-split: module
// evaluation happened during the initial bundle, while the address bar still
// showed the landing URL. Adding React.lazy to the detail route — an ordinary
// performance change — would have moved evaluation to AFTER navigation, at
// which point pathname is no longer /link, the value silently becomes null and
// back-navigation regresses to landing on the share link. Nothing would fail
// loudly.
//
// captureShareLanding() is called from src/index.js before the router mounts,
// so the timing is explicit and greppable. It memoises, so repeat calls are
// harmless, and falls back to reading on first access if it was never called.
let shareLandingIdx;
let shareLandingCaptured = false;

const readShareLandingIdx = () => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const { pathname, search } = window.location;
        if (pathname !== "/link" || !new URLSearchParams(search).get(SHARE_PARAM_KEY)) {
            return null;
        }
        const idx = window.history.state?.idx;
        return typeof idx === "number" ? idx : 0;
    } catch {
        return null;
    }
};

/**
 * Record whether this JS context started on a share link. Call once, as early
 * as possible, before any router navigation can change window.location.
 */
export const captureShareLanding = () => {
    if (!shareLandingCaptured) {
        shareLandingIdx = readShareLandingIdx();
        shareLandingCaptured = true;
    }
    return shareLandingIdx;
};

/** History index of this session's share-link landing entry, or null. */
export const getShareLandingIdx = () => captureShareLanding();

/** Store the readable detail URL (absolute) and open a detail session. */
export const captureDetailUrl = (url) => {
    canonicalDetailUrl = url;
    detailSessionLive = true;
};

/**
 * The readable detail URL, absolute. Falls back to the live address bar so
 * callers are safe before the swap and on routes that never captured anything.
 */
export const getDetailUrl = () =>
    canonicalDetailUrl || (typeof window !== "undefined" ? window.location.href : "");

/** The same URL as a router path (pathname + search), for navigate(). */
export const getDetailPath = () => {
    try {
        const { pathname, search } = new URL(getDetailUrl());
        return `${pathname}${search}`;
    } catch {
        return "/";
    }
};

/** True while a detail page is mounted in this JS context. */
export const isDetailSessionLive = () => detailSessionLive;

/** Called when the detail page unmounts. Keeps the URL, closes the session. */
export const endDetailSession = () => {
    detailSessionLive = false;
};

/**
 * Where "back" should navigate, given the `prevUrl` stored in localStorage.
 *
 * Returns "/" when there is no previous URL, or when it is the share link this
 * session was opened from: /link re-renders the detail page once a detail
 * session exists (see src/routes/index.js), so navigating there would either
 * look like nothing happened or, on the failure paths in hotelDetails, refetch
 * and fail again.
 *
 * Matches on the parsed pathname rather than a string prefix, so an absolute
 * URL is handled and a legitimate path like /search/guid/linked-query is not
 * mistaken for the share route.
 *
 * @param {string|null} prevUrl value read from localStorage
 * @returns {string} a path safe to pass to navigate()
 */
export const getBackTarget = (prevUrl) => {
    if (!prevUrl) {
        return "/";
    }
    try {
        const pathname = prevUrl.startsWith("http")
            ? new URL(prevUrl).pathname
            : prevUrl.split("?")[0];
        return pathname === "/link" ? "/" : prevUrl;
    } catch {
        return "/";
    }
};
